import React, {
    forwardRef,
    useCallback,
    useEffect,
    useImperativeHandle,
    useMemo,
    useRef,
    useState,
} from "react";
import { connect } from "react-redux";
import { Card, Col, Form, Overlay, Popover, Row } from "react-bootstrap";
import DatePicker from "react-datepicker";
import PropTypes from "prop-types";
import Select from "react-select";

import { loading } from "../../../actions/page";
import { getNameChange, saveNameChange } from "../../../actions/matter.name-change";
import { withNavigation } from "../../../utils/wrapper";
import Address from "../../../layout/address";
import CountrySelect from "../../../layout/select/country";
import StateSelect from "../../../layout/select/state";
import GenerationsSelect from "../../../layout/select/generation";
import Hamburger from "../../../layout/kebab";

const PREVIOUS_NAME_CHANGE_REASON_OPTIONS = [
    { value: "OT", label: "Other" },
    { value: "MA", label: "Marriage" },
    { value: "DI", label: "Divorce" },
    { value: "AD", label: "Adoption" },
    { value: "CO", label: "Court order" },
    { value: "PA", label: "Parentage" },
    { value: "IM", label: "Immigration" },
];

const PREVIOUS_NAME_CHANGE_REASON_SELECT_OPTIONS = [
    { value: "", label: "" },
    ...PREVIOUS_NAME_CHANGE_REASON_OPTIONS.map((option) => ({
        ...option,
        label: `${option.label}`,
    })),
];

const normalizePreviousNameChangeReason = (value) => {
    if (!value) return "";

    const normalized = String(value).trim().toUpperCase();
    const byValue = PREVIOUS_NAME_CHANGE_REASON_OPTIONS.find((option) => option.value === normalized);
    if (byValue) return byValue.value;

    const byLabel = PREVIOUS_NAME_CHANGE_REASON_OPTIONS.find((option) => option.label.toUpperCase() === normalized);
    if (byLabel) return byLabel.value;

    return "OT";
};

const findPreviousNameChangeReasonOption = (value) =>
    PREVIOUS_NAME_CHANGE_REASON_SELECT_OPTIONS.find(
        (option) => option.value === normalizePreviousNameChangeReason(value)
    ) || PREVIOUS_NAME_CHANGE_REASON_SELECT_OPTIONS[0];

const emptyPreviousName = () => ({
    firstName: "",
    middleName: "",
    lastName: "",
    generation: "",
    reason: "",
});

const toDateOrNull = (value) => {
    if (!value) return null;
    if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const toIsoOrRaw = (value) => {
    if (!value) return null;
    if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value.toISOString();
    return value;
};

const isPlainObject = (value) =>
    value != null &&
    typeof value === "object" &&
    !Array.isArray(value) &&
    !(value instanceof Date);

const isBlank = (value) => {
    if (value == null) return true;
    if (typeof value === "string") return value.trim() === "";
    if (Array.isArray(value)) return value.length === 0;
    if (isPlainObject(value)) return Object.keys(value).length === 0;
    return false;
};

const mergeBlankOnly = (target, source) => {
    if (source == null) return target;
    if (target == null) return source;

    if (Array.isArray(source)) {
        return isBlank(target) ? source : target;
    }

    if (isPlainObject(source)) {
        const result = { ...(target || {}) };
        for (const key of Object.keys(source)) {
            const targetValue = result[key];
            const sourceValue = source[key];

            if (isPlainObject(sourceValue)) {
                result[key] = mergeBlankOnly(targetValue || {}, sourceValue);
            } else if (Array.isArray(sourceValue)) {
                result[key] = isBlank(targetValue) ? sourceValue : targetValue;
            } else if (isBlank(targetValue) && !isBlank(sourceValue)) {
                result[key] = sourceValue;
            }
        }
        return result;
    }

    return isBlank(target) ? source : target;
};

const getByPath = (obj, path) => {
    if (!path) return undefined;
    return String(path)
        .split(".")
        .reduce((acc, key) => (acc == null ? undefined : acc[key]), obj);
};

const setByPath = (obj, path, value) => {
    const parts = String(path).split(".");
    const result = { ...(obj || {}) };
    let current = result;

    for (let i = 0; i < parts.length - 1; i++) {
        const key = parts[i];
        current[key] = isPlainObject(current[key]) ? { ...current[key] } : {};
        current = current[key];
    }

    current[parts[parts.length - 1]] = value;
    return result;
};

const normalizeComparable = (value) => {
    if (value == null) return "";
    if (value instanceof Date) return value.toISOString();
    if (typeof value === "object") return JSON.stringify(value);
    return String(value).trim();
};

function toDisplayDate(value) {
    if (!value) return null;

    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) return null;

    return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
    });
}

function formatSuggestionValue(value, path = "") {
    if (value == null || value === "") return "[blank]";

    const lowerPath = String(path || "").toLowerCase();

    if (lowerPath.includes("date") || lowerPath.includes("birth")) {
        return toDisplayDate(value) || String(value);
    }

    if (typeof value === "boolean") return value ? "Yes" : "No";
    if (typeof value === "object") return JSON.stringify(value, null, 2);
    return String(value);
}

const normalizePrevious = (previous = []) =>
    (Array.isArray(previous) ? previous : []).map((entry) => ({
        firstName: entry?.firstName ?? "",
        middleName: entry?.middleName ?? "",
        lastName: entry?.lastName ?? "",
        generation: entry?.generation ?? "",
        reason: normalizePreviousNameChangeReason(entry?.reason),
    }));

const normalizeNameChangeFromApi = (item = {}, uuid) => ({
    uuid,
    currentFirstName: item?.currentFirstName ?? "",
    currentMiddleName: item?.currentMiddleName ?? "",
    currentLastName: item?.currentLastName ?? "",
    currentGeneration: item?.currentGeneration ?? "",
    newFirstName: item?.newFirstName ?? "",
    newMiddleName: item?.newMiddleName ?? "",
    newLastName: item?.newLastName ?? "",
    newGeneration: item?.newGeneration ?? "",
    Residence: item?.Residence || {},
    birthState: item?.birthState ?? "",
    birthCountry: item?.birthCountry ?? "US",
    dateOfBirth: toDateOrNull(item?.dateOfBirth),
    birthFirstName: item?.birthFirstName ?? "",
    birthMiddleName: item?.birthMiddleName ?? "",
    birthLastName: item?.birthLastName ?? "",
    birthGeneration: item?.birthGeneration ?? "",
    reason: item?.reason ?? "",
    felon: !!item?.felon,
    incarcerated: !!item?.incarcerated,
    probation: !!item?.probation,
    registered: !!item?.registered,
    parent1FirstName: item?.parent1FirstName ?? "",
    parent1MiddleName: item?.parent1MiddleName ?? "",
    parent1LastName: item?.parent1LastName ?? "",
    parent1Generation: item?.parent1Generation ?? "",
    parent1Maiden: item?.parent1Maiden ?? "",
    parent2FirstName: item?.parent2FirstName ?? "",
    parent2MiddleName: item?.parent2MiddleName ?? "",
    parent2LastName: item?.parent2LastName ?? "",
    parent2Generation: item?.parent2Generation ?? "",
    parent2Maiden: item?.parent2Maiden ?? "",
    meta: {
        ...((item?.meta && typeof item.meta === "object") ? item.meta : {}),
        previous: normalizePrevious(item?.meta?.previous),
    },
});

const firstValue = (...values) =>
    values.find((value) => value !== undefined && value !== null && value !== "");

const mapNameSection = (section = {}) => ({
    firstName: firstValue(section?.firstName, section?.first, section?.FirstName, section?.First) ?? "",
    middleName: firstValue(section?.middleName, section?.middle, section?.MiddleName, section?.Middle) ?? "",
    lastName: firstValue(section?.lastName, section?.last, section?.LastName, section?.Last) ?? "",
    generation: firstValue(section?.generation, section?.suffix, section?.Generation, section?.Suffix) ?? "",
});

const mapAddress = (section = {}) => ({
    line1: section?.line1 ?? section?.Line1 ?? "",
    line2: section?.line2 ?? section?.Line2 ?? "",
    city: section?.city ?? section?.City ?? "",
    county: section?.county ?? section?.County ?? "",
    state: section?.state ?? section?.State ?? "",
    country: section?.country ?? section?.Country ?? section?.options ?? "",
    zipCode: String(section?.zipCode ?? section?.ZipCode ?? section?.PostalCode ?? "").replace(/_/g, "").replace(/-$/, ""),
});

const mapPreviousName = (section = {}) => ({
    ...mapNameSection(section?.Name || section),
    reason: section?.reason ?? section?.Reason ?? "",
});

const mapBoolean = (...values) => {
    const value = firstValue(...values);
    if (value === undefined) return undefined;
    if (typeof value === "boolean") return value;
    if (typeof value === "number") return value !== 0;
    return ["true", "yes", "y", "1"].includes(String(value).trim().toLowerCase());
};

const mapInverseBoolean = (...values) => {
    const value = mapBoolean(...values);
    return value === undefined ? undefined : !value;
};

const compactObject = (obj = {}) =>
    Object.keys(obj || {}).reduce((acc, key) => {
        if (!isBlank(obj[key])) acc[key] = obj[key];
        return acc;
    }, {});

const mapSurveyImportToNameChange = (surveyImport) => {
    const data = surveyImport?.data || {};
    const root = data?.NameChange || data?.nameChange || data?.Namechange || data?.Client || data;
    const client = data?.Client || root?.Client || root;
    const parents = data?.Parents || root?.Parents || {};
    const previous = data?.Previous || root?.Previous || {};

    const current = client?.CurrentName || client?.Current || client?.currentName || client?.current || client?.Name || {};
    const requested = client?.RequestedName || client?.Requested || client?.NewName || client?.New || client?.newName || client?.requestedName || {};
    const birth = client?.BirthName || client?.NameAtBirth || client?.Birth || client?.birthName || client?.nameAtBirth || {};
    const residence = client?.Residence || client?.Address || client?.CurrentAddress || client?.residence || {};
    const birthPlace = client?.Birthplace || client?.BirthPlace || client?.birthPlace || client?.birthplace || {};
    const parent1 = parents?.FirstParent || parents?.firstParent || root?.Parent1 || root?.parent1 || root?.Mother || root?.mother || {};
    const parent2 = parents?.SecondParent || parents?.secondParent || root?.Parent2 || root?.parent2 || root?.Father || root?.father || {};
    const previousSource =
        previous?.Names ||
        previous?.names ||
        root?.PreviousNames?.PreviousNames ||
        root?.PreviousNames ||
        root?.previousNames ||
        root?.previous ||
        [];

    const currentName = mapNameSection(current);
    const requestedName = mapNameSection(requested);
    const birthName = mapNameSection(birth?.Name || birth);
    const parent1Name = mapNameSection(parent1?.Name || parent1);
    const parent2Name = mapNameSection(parent2?.Name || parent2);
    const probationMeta = compactObject({
        court: firstValue(client?.Probation?.Court, client?.probation?.Court, client?.probation?.court),
    });
    const incarceratedMeta = compactObject({
        court: firstValue(client?.Incarcerated?.Court, client?.incarcerated?.Court, client?.incarcerated?.court),
        facility: firstValue(client?.Incarcerated?.Facility, client?.incarcerated?.Facility, client?.incarcerated?.facility),
    });
    const registryMeta = compactObject({
        court: firstValue(client?.Registry?.Court, client?.registry?.Court, client?.registry?.court),
    });
    const convictionMeta = compactObject({
        court: firstValue(client?.Conviction?.Court, client?.conviction?.Court, client?.conviction?.court),
    });

    return {
        currentFirstName: firstValue(root?.currentFirstName, root?.CurrentFirstName, client?.currentFirstName, client?.CurrentFirstName, currentName.firstName) ?? "",
        currentMiddleName: firstValue(root?.currentMiddleName, root?.CurrentMiddleName, client?.currentMiddleName, client?.CurrentMiddleName, currentName.middleName) ?? "",
        currentLastName: firstValue(root?.currentLastName, root?.CurrentLastName, client?.currentLastName, client?.CurrentLastName, currentName.lastName) ?? "",
        currentGeneration: firstValue(root?.currentGeneration, root?.CurrentGeneration, client?.currentGeneration, client?.CurrentGeneration, currentName.generation) ?? "",
        newFirstName: firstValue(root?.newFirstName, root?.NewFirstName, client?.newFirstName, client?.NewFirstName, requestedName.firstName) ?? "",
        newMiddleName: firstValue(root?.newMiddleName, root?.NewMiddleName, client?.newMiddleName, client?.NewMiddleName, requestedName.middleName) ?? "",
        newLastName: firstValue(root?.newLastName, root?.NewLastName, client?.newLastName, client?.NewLastName, requestedName.lastName) ?? "",
        newGeneration: firstValue(root?.newGeneration, root?.NewGeneration, client?.newGeneration, client?.NewGeneration, requestedName.generation) ?? "",
        Residence: mapAddress(residence),
        birthState: birthPlace?.State ?? birthPlace?.state ?? root?.birthState ?? client?.birthState ?? "",
        birthCountry: birthPlace?.Country ?? birthPlace?.country ?? birthPlace?.options ?? root?.birthCountry ?? client?.birthCountry ?? "US",
        dateOfBirth: toIsoOrRaw(birthPlace?.Date || birthPlace?.date || birth?.Date || birth?.date || birth?.dateOfBirth || root?.dateOfBirth || client?.dateOfBirth),
        birthFirstName: firstValue(root?.birthFirstName, root?.BirthFirstName, client?.birthFirstName, client?.BirthFirstName, birthName.firstName) ?? "",
        birthMiddleName: firstValue(root?.birthMiddleName, root?.BirthMiddleName, client?.birthMiddleName, client?.BirthMiddleName, birthName.middleName) ?? "",
        birthLastName: firstValue(root?.birthLastName, root?.BirthLastName, client?.birthLastName, client?.BirthLastName, birthName.lastName) ?? "",
        birthGeneration: firstValue(root?.birthGeneration, root?.BirthGeneration, client?.birthGeneration, client?.BirthGeneration, birthName.generation) ?? "",
        reason: root?.Reason ?? root?.reason ?? client?.Reason ?? client?.reason ?? requested?.Reason ?? requested?.reason ?? "",
        felon: mapInverseBoolean(client?.Conviction?.options, client?.conviction?.options, root?.Felon, root?.felon),
        incarcerated: mapInverseBoolean(client?.Incarcerated?.options, client?.incarcerated?.options, root?.Incarcerated, root?.incarcerated),
        probation: mapInverseBoolean(client?.Probation?.options, client?.probation?.options, root?.Probation, root?.probation),
        registered: mapInverseBoolean(client?.Registry?.options, client?.registry?.options, root?.Registered, root?.registered),
        parent1FirstName: firstValue(root?.parent1FirstName, root?.Parent1FirstName, parent1Name.firstName) ?? "",
        parent1MiddleName: firstValue(root?.parent1MiddleName, root?.Parent1MiddleName, parent1Name.middleName) ?? "",
        parent1LastName: firstValue(root?.parent1LastName, root?.Parent1LastName, parent1Name.lastName) ?? "",
        parent1Generation: firstValue(root?.parent1Generation, root?.Parent1Generation, parent1Name.generation) ?? "",
        parent1Maiden: parents?.FirstParentMaiden ?? parents?.firstParentMaiden ?? parent1?.Maiden ?? parent1?.maiden ?? parent1?.maidenName ?? "",
        parent2FirstName: firstValue(root?.parent2FirstName, root?.Parent2FirstName, parent2Name.firstName) ?? "",
        parent2MiddleName: firstValue(root?.parent2MiddleName, root?.Parent2MiddleName, parent2Name.middleName) ?? "",
        parent2LastName: firstValue(root?.parent2LastName, root?.Parent2LastName, parent2Name.lastName) ?? "",
        parent2Generation: firstValue(root?.parent2Generation, root?.Parent2Generation, parent2Name.generation) ?? "",
        parent2Maiden: parents?.SecondParentMaiden ?? parents?.secondParentMaiden ?? parent2?.Maiden ?? parent2?.maiden ?? parent2?.maidenName ?? "",
        meta: {
            ...(Object.keys(probationMeta).length ? { probation: probationMeta } : {}),
            ...(Object.keys(incarceratedMeta).length ? { incarcerated: incarceratedMeta } : {}),
            ...(Object.keys(registryMeta).length ? { registry: registryMeta } : {}),
            ...(Object.keys(convictionMeta).length ? { conviction: convictionMeta } : {}),
            previous: normalizePrevious(
                Array.isArray(previousSource)
                    ? previousSource.map(mapPreviousName)
                    : []
            ),
        },
    };
};

const flattenNameChangeSuggestions = (nameChange = {}) => ({
    currentFirstName: nameChange?.currentFirstName,
    currentMiddleName: nameChange?.currentMiddleName,
    currentLastName: nameChange?.currentLastName,
    currentGeneration: nameChange?.currentGeneration,
    newFirstName: nameChange?.newFirstName,
    newMiddleName: nameChange?.newMiddleName,
    newLastName: nameChange?.newLastName,
    newGeneration: nameChange?.newGeneration,
    birthState: nameChange?.birthState,
    birthCountry: nameChange?.birthCountry,
    dateOfBirth: nameChange?.dateOfBirth,
    birthFirstName: nameChange?.birthFirstName,
    birthMiddleName: nameChange?.birthMiddleName,
    birthLastName: nameChange?.birthLastName,
    birthGeneration: nameChange?.birthGeneration,
    reason: nameChange?.reason,
    felon: nameChange?.felon,
    incarcerated: nameChange?.incarcerated,
    probation: nameChange?.probation,
    registered: nameChange?.registered,
    "meta.conviction.court": nameChange?.meta?.conviction?.court,
    "meta.incarcerated.court": nameChange?.meta?.incarcerated?.court,
    "meta.incarcerated.facility": nameChange?.meta?.incarcerated?.facility,
    "meta.probation.court": nameChange?.meta?.probation?.court,
    "meta.registry.court": nameChange?.meta?.registry?.court,
    parent1FirstName: nameChange?.parent1FirstName,
    parent1MiddleName: nameChange?.parent1MiddleName,
    parent1LastName: nameChange?.parent1LastName,
    parent1Generation: nameChange?.parent1Generation,
    parent1Maiden: nameChange?.parent1Maiden,
    parent2FirstName: nameChange?.parent2FirstName,
    parent2MiddleName: nameChange?.parent2MiddleName,
    parent2LastName: nameChange?.parent2LastName,
    parent2Generation: nameChange?.parent2Generation,
    parent2Maiden: nameChange?.parent2Maiden,
    Residence: nameChange?.Residence || {},
});

const flattenPreviousSuggestions = (entry = {}) => ({
    firstName: entry?.firstName,
    middleName: entry?.middleName,
    lastName: entry?.lastName,
    generation: entry?.generation,
    reason: entry?.reason,
});

const ReviewLabel = ({ label, path, value = undefined, onApply = null, onReject = null, inline = false }) => {
    const LabelComponent = inline ? "span" : Form.Label;
    const [menu, setMenu] = useState({
        show: false,
        target: null,
    });

    const onOpen = useCallback((event) => {
        event.preventDefault();
        event.stopPropagation();
        setMenu({
            show: true,
            target: event.currentTarget,
        });
    }, []);

    const onClose = useCallback(() => {
        setMenu({
            show: false,
            target: null,
        });
    }, []);

    const onApplyClick = useCallback(() => {
        onApply?.(path, value);
        onClose();
    }, [onApply, path, value, onClose]);

    const onRejectClick = useCallback(() => {
        onReject?.(path);
        onClose();
    }, [onReject, path, onClose]);

    return (
        <>
            <LabelComponent>
                {label}
                {value !== undefined ? (
                    <button
                        type="button"
                        onClick={onOpen}
                        style={{
                            border: 0,
                            background: "transparent",
                            padding: 0,
                            marginLeft: 6,
                            lineHeight: 1,
                            fontSize: "0.95rem",
                            cursor: "pointer",
                        }}
                        title="Review imported survey value"
                    >
                        &#10024;
                    </button>
                ) : null}
            </LabelComponent>
            <Overlay
                target={menu.target}
                show={menu.show}
                placement="bottom-end"
                rootClose
                onHide={onClose}
            >
                {(overlayProps) => (
                    <Popover {...overlayProps}>
                        <Popover.Header as="h3">Imported survey value</Popover.Header>
                        <Popover.Body>
                            <div className="small text-muted mb-2">{path}</div>
                            <div className="mb-3" style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                                {formatSuggestionValue(value, path)}
                            </div>
                            <div className="d-flex gap-2">
                                <button type="button" className="btn btn-primary btn-sm" onClick={onApplyClick}>
                                    Accept
                                </button>
                                <button type="button" className="btn btn-outline-secondary btn-sm" onClick={onRejectClick}>
                                    Reject
                                </button>
                            </div>
                        </Popover.Body>
                    </Popover>
                )}
            </Overlay>
        </>
    );
};

ReviewLabel.propTypes = {
    label: PropTypes.string.isRequired,
    path: PropTypes.string.isRequired,
    value: PropTypes.any,
    onApply: PropTypes.func,
    onReject: PropTypes.func,
    inline: PropTypes.bool,
};

const Field = ({ name, label, value, onChange, as, suggestion, onApplySuggestion, onRejectSuggestion }) => (
    <div className="mb-3 top-label">
        <Form.Control
            as={as}
            name={name}
            value={value ?? ""}
            onChange={(e) => onChange(name, e.target.value)}
        />
        <ReviewLabel label={label} path={name} value={suggestion} onApply={onApplySuggestion} onReject={onRejectSuggestion} />
    </div>
);

Field.propTypes = {
    name: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    onChange: PropTypes.func.isRequired,
    as: PropTypes.string,
    suggestion: PropTypes.any,
    onApplySuggestion: PropTypes.func,
    onRejectSuggestion: PropTypes.func,
};

Field.defaultProps = {
    value: "",
    as: undefined,
    suggestion: undefined,
    onApplySuggestion: null,
    onRejectSuggestion: null,
};

const GenerationField = ({ name, label = "SUFFIX", value = "", onChange, suggestion, onApplySuggestion, onRejectSuggestion }) => (
    <div className="mb-3 top-label">
        <GenerationsSelect
            name={name}
            value={value ?? ""}
            onChange={(e) => onChange(name, e?.value ?? "")}
            placeholder=""
        />
        <ReviewLabel label={label} path={name} value={suggestion} onApply={onApplySuggestion} onReject={onRejectSuggestion} />
    </div>
);

GenerationField.propTypes = {
    name: PropTypes.string.isRequired,
    label: PropTypes.string,
    value: PropTypes.string,
    onChange: PropTypes.func.isRequired,
    suggestion: PropTypes.any,
    onApplySuggestion: PropTypes.func,
    onRejectSuggestion: PropTypes.func,
};

const PreviousNameReasonField = ({ name, label, value = "", onChange, suggestion, onApplySuggestion, onRejectSuggestion }) => (
    <div className="mb-3 top-label">
        <Select
            name={name}
            value={findPreviousNameChangeReasonOption(value)}
            onChange={(option) => onChange(name, option?.value ?? "")}
            classNamePrefix="react-select"
            placeholder=""
            options={PREVIOUS_NAME_CHANGE_REASON_SELECT_OPTIONS}
            isClearable={false}
        />
        <ReviewLabel label={label} path={name} value={suggestion} onApply={onApplySuggestion} onReject={onRejectSuggestion} />
    </div>
);

PreviousNameReasonField.propTypes = {
    name: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    value: PropTypes.string,
    onChange: PropTypes.func.isRequired,
    suggestion: PropTypes.any,
    onApplySuggestion: PropTypes.func,
    onRejectSuggestion: PropTypes.func,
};

const optionRowStyle = { minHeight: "68px" };
const optionSwitchColumnStyle = {
    minHeight: "68px",
    display: "flex",
    alignItems: "center",
};

const NameChange = forwardRef(function NameChange(props, ref) {
    const {
        uuid,
        surveyImport,
        loading,
        getNameChange,
        saveNameChange,
    } = props;

    const loadingDataRef = useRef(false);
    const appliedImportKeyRef = useRef(null);

    const [nameChange, setNameChange] = useState(normalizeNameChangeFromApi({}, uuid));
    const [changed, setChanged] = useState(false);
    const [rejectedSuggestions, setRejectedSuggestions] = useState({});
    const [previousMenu, setPreviousMenu] = useState({
        show: false,
        target: null,
    });
    const [previousItemMenu, setPreviousItemMenu] = useState({
        show: false,
        target: null,
        index: null,
    });

    const markChanged = useCallback((patch) => {
        setNameChange((prev) => ({
            ...(prev || {}),
            ...(patch || {}),
        }));
        setChanged(true);
    }, []);

    const mappedSurveyNameChange = useMemo(() => {
        return surveyImport?.data ? mapSurveyImportToNameChange(surveyImport) : null;
    }, [surveyImport]);

    const applySurveyImport = useCallback((baseNameChange = {}) => {
        if (!surveyImport?.data) return baseNameChange;

        const mapped = mapSurveyImportToNameChange(surveyImport);
        return mergeBlankOnly(baseNameChange || {}, mapped);
    }, [surveyImport]);

    const makeNameChangeSuggestions = useCallback(() => {
        const flat = flattenNameChangeSuggestions(mappedSurveyNameChange || {});
        const current = nameChange || {};

        return Object.keys(flat).reduce((acc, key) => {
            const suggestionKey = `NameChange.${key}`;
            const incoming = flat[key];
            const currentValue = getByPath(current, key);

            if (key === "Residence") {
                const nested = Object.keys(incoming || {}).reduce((residenceAcc, path) => {
                    const nestedSuggestionKey = `${suggestionKey}.${path}`;
                    const incomingValue = incoming?.[path];
                    const nestedCurrentValue = currentValue?.[path];

                    if (incomingValue === undefined) return residenceAcc;
                    if (rejectedSuggestions[nestedSuggestionKey]) return residenceAcc;
                    if (normalizeComparable(nestedCurrentValue) === normalizeComparable(incomingValue)) return residenceAcc;
                    if (isBlank(incomingValue)) return residenceAcc;

                    residenceAcc[path] = incomingValue;
                    return residenceAcc;
                }, {});

                if (Object.keys(nested).length > 0) acc[key] = nested;
                return acc;
            }

            if (incoming === undefined) return acc;
            if (rejectedSuggestions[suggestionKey]) return acc;
            if (normalizeComparable(currentValue) === normalizeComparable(incoming)) return acc;
            if (isBlank(incoming)) return acc;

            acc[key] = incoming;
            return acc;
        }, {});
    }, [mappedSurveyNameChange, nameChange, rejectedSuggestions]);

    const nameChangeSuggestions = useMemo(
        () => makeNameChangeSuggestions(),
        [makeNameChangeSuggestions]
    );

    const makePreviousSuggestions = useCallback(() => {
        const mappedPrevious = normalizePrevious(mappedSurveyNameChange?.meta?.previous);
        const currentPrevious = normalizePrevious(nameChange?.meta?.previous);

        return mappedPrevious.map((mappedEntry, index) => {
            const flat = flattenPreviousSuggestions(mappedEntry || {});
            const currentEntry = currentPrevious[index] || {};

            return Object.keys(flat).reduce((acc, key) => {
                const suggestionKey = `Previous.${index}.${key}`;
                const incoming = flat[key];
                const currentValue = getByPath(currentEntry, key);

                if (incoming === undefined) return acc;
                if (rejectedSuggestions[suggestionKey]) return acc;
                if (normalizeComparable(currentValue) === normalizeComparable(incoming)) return acc;
                if (isBlank(incoming)) return acc;

                acc[key] = incoming;
                return acc;
            }, {});
        });
    }, [mappedSurveyNameChange, nameChange, rejectedSuggestions]);

    const previousSuggestions = useMemo(
        () => makePreviousSuggestions(),
        [makePreviousSuggestions]
    );

    const applyNameChangeSuggestion = useCallback((path, value) => {
        setNameChange((prev) => setByPath(prev || {}, path, value));

        setRejectedSuggestions((prev) => {
            const next = { ...(prev || {}) };
            delete next[`NameChange.${path}`];
            return next;
        });

        setChanged(true);
    }, []);

    const rejectNameChangeSuggestion = useCallback((path) => {
        setRejectedSuggestions((prev) => ({
            ...(prev || {}),
            [`NameChange.${path}`]: true,
        }));
    }, []);

    const applyPreviousSuggestion = useCallback((index, path, value) => {
        setNameChange((prev) => {
            const previous = normalizePrevious(prev?.meta?.previous);
            previous[index] = setByPath(
                previous[index] || emptyPreviousName(),
                path,
                path === "reason" ? normalizePreviousNameChangeReason(value) : value
            );

            return {
                ...(prev || {}),
                meta: {
                    ...((prev?.meta && typeof prev.meta === "object") ? prev.meta : {}),
                    previous,
                },
            };
        });

        setRejectedSuggestions((prev) => {
            const next = { ...(prev || {}) };
            delete next[`Previous.${index}.${path}`];
            return next;
        });

        setChanged(true);
    }, []);

    const rejectPreviousSuggestion = useCallback((index, path) => {
        setRejectedSuggestions((prev) => ({
            ...(prev || {}),
            [`Previous.${index}.${path}`]: true,
        }));
    }, []);

    const onFieldChange = useCallback((name, value) => {
        markChanged({ [name]: value });
    }, [markChanged]);

    const onMetaFieldChange = useCallback((name, value) => {
        setNameChange((prev) => setByPath(prev || {}, name, value));
        setChanged(true);
    }, []);

    const onDateChange = useCallback((date) => {
        markChanged({ dateOfBirth: date ?? null });
    }, [markChanged]);

    const onBirthCountryChange = useCallback((option) => {
        markChanged({
            birthCountry: option?.value ?? "",
            ...(option?.value !== "US" ? { birthState: "" } : {}),
        });
    }, [markChanged]);

    const onBirthStateChange = useCallback((value) => {
        markChanged({ birthState: value ?? "" });
    }, [markChanged]);

    const onResidenceChange = useCallback((Residence) => {
        markChanged({ Residence: Residence || {} });
    }, [markChanged]);

    const onPreviousChange = useCallback((index, name, value) => {
        setNameChange((prev) => {
            const previous = normalizePrevious(prev?.meta?.previous);
            previous[index] = {
                ...(previous[index] || emptyPreviousName()),
                [name]: name === "reason" ? normalizePreviousNameChangeReason(value) : value,
            };

            return {
                ...(prev || {}),
                meta: {
                    ...((prev?.meta && typeof prev.meta === "object") ? prev.meta : {}),
                    previous,
                },
            };
        });
        setChanged(true);
    }, []);

    const onPreviousAdd = useCallback((e) => {
        e?.preventDefault?.();
        setNameChange((prev) => ({
            ...(prev || {}),
            meta: {
                ...((prev?.meta && typeof prev.meta === "object") ? prev.meta : {}),
                previous: [
                    ...normalizePrevious(prev?.meta?.previous),
                    emptyPreviousName(),
                ],
            },
        }));
        setPreviousMenu({
            show: false,
            target: null,
        });
        setChanged(true);
    }, []);

    const onPreviousMenuOpen = useCallback((e) => {
        e?.preventDefault?.();
        setPreviousMenu({
            show: true,
            target: e.currentTarget,
        });
    }, []);

    const onPreviousMenuClose = useCallback(() => {
        setPreviousMenu({
            show: false,
            target: null,
        });
    }, []);

    const onPreviousItemMenuOpen = useCallback((e, index) => {
        e?.preventDefault?.();
        setPreviousItemMenu({
            show: true,
            target: e.currentTarget,
            index,
        });
    }, []);

    const onPreviousItemMenuClose = useCallback(() => {
        setPreviousItemMenu({
            show: false,
            target: null,
            index: null,
        });
    }, []);

    const onPreviousRemove = useCallback((e, index = previousItemMenu.index) => {
        e?.preventDefault?.();
        setNameChange((prev) => ({
            ...(prev || {}),
            meta: {
                ...((prev?.meta && typeof prev.meta === "object") ? prev.meta : {}),
                previous: normalizePrevious(prev?.meta?.previous).filter((_, itemIndex) => itemIndex !== index),
            },
        }));
        onPreviousItemMenuClose();
        setChanged(true);
    }, [previousItemMenu.index, onPreviousItemMenuClose]);

    const loadData = useCallback(async () => {
        if (!uuid || loadingDataRef.current) return;
        loadingDataRef.current = true;
        loading(true);

        try {
            const resp = await getNameChange({ uuid });
            const normalized = resp?.success && resp?.item
                ? normalizeNameChangeFromApi(resp.item, uuid)
                : normalizeNameChangeFromApi({}, uuid);
            const next = applySurveyImport(normalized);

            setNameChange(next);
            setChanged(false);

            if (surveyImport?.key || surveyImport?.title || surveyImport?.matterUuid || surveyImport?.mode) {
                appliedImportKeyRef.current =
                    surveyImport?.key ||
                    `${surveyImport?.title || ""}:${surveyImport?.matterUuid || ""}:${surveyImport?.mode || ""}`;
            }
        } finally {
            loading(false);
            loadingDataRef.current = false;
        }
    }, [loading, getNameChange, uuid, applySurveyImport, surveyImport]);

    useEffect(() => {
        setNameChange(normalizeNameChangeFromApi({}, uuid));
        setChanged(false);
        setRejectedSuggestions({});
        appliedImportKeyRef.current = null;
    }, [uuid]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    useEffect(() => {
        if (!surveyImport?.data) return;
        if (loadingDataRef.current) return;

        const nextKey =
            surveyImport?.key ||
            `${surveyImport?.title || ""}:${surveyImport?.matterUuid || ""}:${surveyImport?.mode || ""}`;

        if (appliedImportKeyRef.current === nextKey) return;

        setNameChange((prev) => applySurveyImport(prev || normalizeNameChangeFromApi({}, uuid)));
        appliedImportKeyRef.current = nextKey;
    }, [surveyImport, applySurveyImport, uuid]);

    const saveChanges = useCallback(async (e) => {
        e?.preventDefault?.();
        loading(true);

        try {
            const payload = {
                ...(nameChange || {}),
                uuid,
                meta: {
                    ...((nameChange?.meta && typeof nameChange.meta === "object") ? nameChange.meta : {}),
                    previous: normalizePrevious(nameChange?.meta?.previous),
                },
            };

            await saveNameChange(payload);
            await loadData();
            setChanged(false);
        } finally {
            loading(false);
        }
    }, [loading, saveNameChange, nameChange, uuid, loadData]);

    const validate = useCallback(() => {
        return { ok: true };
    }, []);

    const getData = useCallback(() => {
        return { nameChange };
    }, [nameChange]);

    useImperativeHandle(
        ref,
        () => ({
            saveChanges,
            validate,
            getData,
            changed,
        }),
        [saveChanges, validate, getData, changed]
    );

    const previous = normalizePrevious(nameChange?.meta?.previous);
    const reviewPropsFor = useCallback((path) => ({
        suggestion: nameChangeSuggestions?.[path],
        onApplySuggestion: applyNameChangeSuggestion,
        onRejectSuggestion: rejectNameChangeSuggestion,
    }), [nameChangeSuggestions, applyNameChangeSuggestion, rejectNameChangeSuggestion]);
    const previousReviewPropsFor = useCallback((index, path) => ({
        suggestion: previousSuggestions?.[index]?.[path],
        onApplySuggestion: (suggestionPath, value) => applyPreviousSuggestion(index, suggestionPath, value),
        onRejectSuggestion: (suggestionPath) => rejectPreviousSuggestion(index, suggestionPath),
    }), [previousSuggestions, applyPreviousSuggestion, rejectPreviousSuggestion]);

    return (
        <Card body className="mb-5">
                <Row className="g-3">
                    <Col xs="12">
                        <h2 className="small-title">Current Name</h2>
                    </Col>
                    <Col md="3">
                        <Field name="currentFirstName" label="FIRST NAME" value={nameChange.currentFirstName} onChange={onFieldChange} {...reviewPropsFor("currentFirstName")} />
                    </Col>
                    <Col md="3">
                        <Field name="currentMiddleName" label="MIDDLE NAME" value={nameChange.currentMiddleName} onChange={onFieldChange} {...reviewPropsFor("currentMiddleName")} />
                    </Col>
                    <Col md="4">
                        <Field name="currentLastName" label="LAST NAME" value={nameChange.currentLastName} onChange={onFieldChange} {...reviewPropsFor("currentLastName")} />
                    </Col>
                    <Col md="2">
                        <GenerationField name="currentGeneration" value={nameChange.currentGeneration} onChange={onFieldChange} {...reviewPropsFor("currentGeneration")} />
                    </Col>
                </Row>

                <Row className="g-3">
                    <Col xs="12">
                        <h2 className="small-title">Requested Name</h2>
                    </Col>
                    <Col md="3">
                        <Field name="newFirstName" label="FIRST NAME" value={nameChange.newFirstName} onChange={onFieldChange} {...reviewPropsFor("newFirstName")} />
                    </Col>
                    <Col md="3">
                        <Field name="newMiddleName" label="MIDDLE NAME" value={nameChange.newMiddleName} onChange={onFieldChange} {...reviewPropsFor("newMiddleName")} />
                    </Col>
                    <Col md="4">
                        <Field name="newLastName" label="LAST NAME" value={nameChange.newLastName} onChange={onFieldChange} {...reviewPropsFor("newLastName")} />
                    </Col>
                    <Col md="2">
                        <GenerationField name="newGeneration" value={nameChange.newGeneration} onChange={onFieldChange} {...reviewPropsFor("newGeneration")} />
                    </Col>
                </Row>

                <Row className="g-3">
                    <Col md="12">
                        <Field as="textarea" name="reason" label="REASON FOR NAME CHANGE" value={nameChange.reason} onChange={onFieldChange} {...reviewPropsFor("reason")} />
                    </Col>
                </Row>
                <Row className="g-3">
                    <Col xs="12">
                        <h2 className="small-title">Name At Birth</h2>
                    </Col>
                    <Col md="3">
                        <Field name="birthFirstName" label="FIRST NAME" value={nameChange.birthFirstName} onChange={onFieldChange} {...reviewPropsFor("birthFirstName")} />
                    </Col>
                    <Col md="3">
                        <Field name="birthMiddleName" label="MIDDLE NAME" value={nameChange.birthMiddleName} onChange={onFieldChange} {...reviewPropsFor("birthMiddleName")} />
                    </Col>
                    <Col md="4">
                        <Field name="birthLastName" label="LAST NAME" value={nameChange.birthLastName} onChange={onFieldChange} {...reviewPropsFor("birthLastName")} />
                    </Col>
                    <Col md="2">
                        <GenerationField name="birthGeneration" value={nameChange.birthGeneration} onChange={onFieldChange} {...reviewPropsFor("birthGeneration")} />
                    </Col>
                    <Col md="4">
                        <div className="mb-3 top-label">
                            <DatePicker
                                isClearable
                                id="dateOfBirth"
                                name="dateOfBirth"
                                className="form-control"
                                selected={toDateOrNull(nameChange.dateOfBirth)}
                                onChange={onDateChange}
                            />
                            <ReviewLabel
                                label="DATE OF BIRTH"
                                path="dateOfBirth"
                                value={nameChangeSuggestions?.dateOfBirth}
                                onApply={applyNameChangeSuggestion}
                                onReject={rejectNameChangeSuggestion}
                            />
                        </div>
                    </Col>
                    <Col md="4">
                        <div className="mb-3 top-label">
                            <CountrySelect
                                name="birthCountry"
                                value={nameChange.birthCountry || "US"}
                                onChange={onBirthCountryChange}
                                placeholder=""
                            />
                            <ReviewLabel
                                label="BIRTH COUNTRY"
                                path="birthCountry"
                                value={nameChangeSuggestions?.birthCountry}
                                onApply={applyNameChangeSuggestion}
                                onReject={rejectNameChangeSuggestion}
                            />
                        </div>
                    </Col>
                    {nameChange.birthCountry === "US" ? (
                    <Col md="4">
                        <div className="mb-3 top-label">
                            <StateSelect
                                isClearable
                                name="birthState"
                                value={nameChange.birthState}
                                onChange={onBirthStateChange}
                                placeholder=""
                            />
                            <ReviewLabel
                                label="BIRTH STATE"
                                path="birthState"
                                value={nameChangeSuggestions?.birthState}
                                onApply={applyNameChangeSuggestion}
                                onReject={rejectNameChangeSuggestion}
                            />
                        </div>
                    </Col>
                    ) : null}
                </Row>
                <Row className="g-3">
                    <Col xs="12">
                        <h2 className="small-title">Currently Resides At (Address)</h2>
                    </Col>
                    <Col xs="12">
                        <Address
                            {...(nameChange.Residence || {})}
                            suggestions={nameChangeSuggestions?.Residence || {}}
                            onApplySuggestion={(path, value) => applyNameChangeSuggestion(`Residence.${path}`, value)}
                            onRejectSuggestion={(path) => rejectNameChangeSuggestion(`Residence.${path}`)}
                            onChange={onResidenceChange}
                        />
                    </Col>
                </Row>
                <Row className="g-3">
                    <Col xs="11">
                        <h2 className="small-title">
                            Previous Names &nbsp;
                        </h2>
                    </Col>
                    <Col xs="1">
                        <Hamburger
                            variant="link"
                            vertical={false}
                            onClick={onPreviousMenuOpen}
                        />
                    </Col>
                    {previous.map((entry, index) => (
                        <Col
                            xs="12"
                            key={`previous_${index}`}
                            style={{
                                backgroundColor: index % 2 === 0 ? "#e9f8fb" : "white",
                                padding: "20px",
                            }}
                        >
                            <Row className="g-3">
                                <Col xs="11">
                                    <h3 className="small-title">Previous Name #{index + 1}</h3>
                                </Col>
                                <Col xs="1">
                                    <Hamburger
                                        variant="link"
                                        vertical={false}
                                        onClick={(e) => onPreviousItemMenuOpen(e, index)}
                                    />
                                </Col>
                            </Row>
                            <Row className="g-3 align-items-start">
                                <Col md="3">
                                    <Field name="firstName" label="FIRST NAME" value={entry.firstName} onChange={(name, value) => onPreviousChange(index, name, value)} {...previousReviewPropsFor(index, "firstName")} />
                                </Col>
                                <Col md="2">
                                    <Field name="middleName" label="MIDDLE NAME" value={entry.middleName} onChange={(name, value) => onPreviousChange(index, name, value)} {...previousReviewPropsFor(index, "middleName")} />
                                </Col>
                                <Col md="3">
                                    <Field name="lastName" label="LAST NAME" value={entry.lastName} onChange={(name, value) => onPreviousChange(index, name, value)} {...previousReviewPropsFor(index, "lastName")} />
                                </Col>
                                <Col md="2">
                                    <GenerationField name="generation" value={entry.generation} onChange={(name, value) => onPreviousChange(index, name, value)} {...previousReviewPropsFor(index, "generation")} />
                                </Col>
                                <Col md="2">
                                    <PreviousNameReasonField name="reason" label="REASON USED" value={entry.reason} onChange={(name, value) => onPreviousChange(index, name, value)} {...previousReviewPropsFor(index, "reason")} />
                                </Col>
                            </Row>
                        </Col>
                    ))}
                </Row>
                <Row className="g-3">
                    <Col xs="12">
                        <h2 className="small-title">Parent Information</h2>
                    </Col>
                    <Col md="3">
                        <Field name="parent1FirstName" label="PARENT 1 FIRST NAME" value={nameChange.parent1FirstName} onChange={onFieldChange} {...reviewPropsFor("parent1FirstName")} />
                    </Col>
                    <Col md="3">
                        <Field name="parent1MiddleName" label="PARENT 1 MIDDLE NAME" value={nameChange.parent1MiddleName} onChange={onFieldChange} {...reviewPropsFor("parent1MiddleName")} />
                    </Col>
                    <Col md="3">
                        <Field name="parent1LastName" label="PARENT 1 LAST NAME" value={nameChange.parent1LastName} onChange={onFieldChange} {...reviewPropsFor("parent1LastName")} />
                    </Col>
                    <Col md="1">
                        <GenerationField name="parent1Generation" value={nameChange.parent1Generation} onChange={onFieldChange} {...reviewPropsFor("parent1Generation")} />
                    </Col>
                    <Col md="2">
                        <Field name="parent1Maiden" label="MAIDEN NAME" value={nameChange.parent1Maiden} onChange={onFieldChange} {...reviewPropsFor("parent1Maiden")} />
                    </Col>
                    <Col md="3">
                        <Field name="parent2FirstName" label="PARENT 2 FIRST NAME" value={nameChange.parent2FirstName} onChange={onFieldChange} {...reviewPropsFor("parent2FirstName")} />
                    </Col>
                    <Col md="3">
                        <Field name="parent2MiddleName" label="PARENT 2 MIDDLE NAME" value={nameChange.parent2MiddleName} onChange={onFieldChange} {...reviewPropsFor("parent2MiddleName")} />
                    </Col>
                    <Col md="3">
                        <Field name="parent2LastName" label="PARENT 2 LAST NAME" value={nameChange.parent2LastName} onChange={onFieldChange} {...reviewPropsFor("parent2LastName")} />
                    </Col>
                    <Col md="1">
                        <GenerationField name="parent2Generation" value={nameChange.parent2Generation} onChange={onFieldChange} {...reviewPropsFor("parent2Generation")} />
                    </Col>
                    <Col md="2">
                        <Field name="parent2Maiden" label="MAIDEN NAME" value={nameChange.parent2Maiden} onChange={onFieldChange} {...reviewPropsFor("parent2Maiden")} />
                    </Col>
                </Row>
                <Row className="g-3 align-items-start" style={optionRowStyle}>
                    <Col md="3" style={optionSwitchColumnStyle}>
                        <Form.Check
                            type="switch"
                            id="nameChangeFelon"
                            label={(
                                <ReviewLabel
                                    inline
                                    label="Felon"
                                    path="felon"
                                    value={nameChangeSuggestions?.felon}
                                    onApply={applyNameChangeSuggestion}
                                    onReject={rejectNameChangeSuggestion}
                                />
                            )}
                            checked={!!nameChange.felon}
                            onChange={(e) => onFieldChange("felon", e.target.checked)}
                        />
                    </Col>
                </Row>
                <Row className="g-3 align-items-start" style={optionRowStyle}>
                    <Col md="3" style={optionSwitchColumnStyle}>
                        <Form.Check
                            type="switch"
                            id="nameChangeIncarcerated"
                            label={(
                                <ReviewLabel
                                    inline
                                    label="Incarcerated"
                                    path="incarcerated"
                                    value={nameChangeSuggestions?.incarcerated}
                                    onApply={applyNameChangeSuggestion}
                                    onReject={rejectNameChangeSuggestion}
                                />
                            )}
                            checked={!!nameChange.incarcerated}
                            onChange={(e) => onFieldChange("incarcerated", e.target.checked)}
                        />
                    </Col>
                    {nameChange.incarcerated ? (
                        <>
                            <Col md="4">
                                <Field
                                    name="meta.incarcerated.court"
                                    label="INCARCERATION COURT"
                                    value={nameChange?.meta?.incarcerated?.court}
                                    onChange={onMetaFieldChange}
                                    {...reviewPropsFor("meta.incarcerated.court")}
                                />
                            </Col>
                            <Col md="4">
                                <Field
                                    name="meta.incarcerated.facility"
                                    label="INCARCERATION FACILITY"
                                    value={nameChange?.meta?.incarcerated?.facility}
                                    onChange={onMetaFieldChange}
                                    {...reviewPropsFor("meta.incarcerated.facility")}
                                />
                            </Col>
                        </>
                    ) : null}
                </Row>
                <Row className="g-3 align-items-start" style={optionRowStyle}>
                    <Col md="3" style={optionSwitchColumnStyle}>
                        <Form.Check
                            type="switch"
                            id="nameChangeProbation"
                            label={(
                                <ReviewLabel
                                    inline
                                    label="Probation"
                                    path="probation"
                                    value={nameChangeSuggestions?.probation}
                                    onApply={applyNameChangeSuggestion}
                                    onReject={rejectNameChangeSuggestion}
                                />
                            )}
                            checked={!!nameChange.probation}
                            onChange={(e) => onFieldChange("probation", e.target.checked)}
                        />
                    </Col>
                    {nameChange.probation ? (
                        <Col md="4">
                            <Field
                                name="meta.probation.court"
                                label="PROBATION COURT"
                                value={nameChange?.meta?.probation?.court}
                                onChange={onMetaFieldChange}
                                {...reviewPropsFor("meta.probation.court")}
                            />
                        </Col>
                    ) : null}
                </Row>
                <Row className="g-3 align-items-start" style={optionRowStyle}>
                    <Col md="3" style={optionSwitchColumnStyle}>
                        <Form.Check
                            type="switch"
                            id="nameChangeRegistered"
                            label={(
                                <ReviewLabel
                                    inline
                                    label="Registered"
                                    path="registered"
                                    value={nameChangeSuggestions?.registered}
                                    onApply={applyNameChangeSuggestion}
                                    onReject={rejectNameChangeSuggestion}
                                />
                            )}
                            checked={!!nameChange.registered}
                            onChange={(e) => onFieldChange("registered", e.target.checked)}
                        />
                    </Col>
                    {nameChange.registered ? (
                        <Col md="4">
                            <Field
                                name="meta.registry.court"
                                label="REGISTRY COURT"
                                value={nameChange?.meta?.registry?.court}
                                onChange={onMetaFieldChange}
                                {...reviewPropsFor("meta.registry.court")}
                            />
                        </Col>
                    ) : null}
                </Row>
            <Overlay
                target={previousMenu.target}
                show={previousMenu.show}
                placement="bottom-end"
                rootClose
                onHide={onPreviousMenuClose}
            >
                {(overlayProps) => (
                    <Popover {...overlayProps}>
                        <Popover.Body>
                            <Row className="mb-2">
                                <Col className="d-flex flex-row mt-1">
                                    <button
                                        type="button"
                                        className="btn btn-link p-0 me-2 btn-sm small text-start"
                                        onClick={onPreviousAdd}
                                    >
                                        New
                                    </button>
                                </Col>
                            </Row>
                        </Popover.Body>
                    </Popover>
                )}
            </Overlay>
            <Overlay
                target={previousItemMenu.target}
                show={previousItemMenu.show}
                placement="bottom-end"
                rootClose
                onHide={onPreviousItemMenuClose}
            >
                {(overlayProps) => (
                    <Popover {...overlayProps}>
                        <Popover.Body>
                            <Row className="mb-2">
                                <Col className="d-flex flex-row mt-1">
                                    <button
                                        type="button"
                                        className="btn btn-link p-0 me-2 btn-sm small text-start"
                                        onClick={(e) => onPreviousRemove(e)}
                                    >
                                        Remove
                                    </button>
                                </Col>
                            </Row>
                        </Popover.Body>
                    </Popover>
                )}
            </Overlay>
        </Card>
    );
});

NameChange.propTypes = {
    uuid: PropTypes.string,
    surveyImport: PropTypes.object,
    loading: PropTypes.func.isRequired,
    getNameChange: PropTypes.func.isRequired,
    saveNameChange: PropTypes.func.isRequired,
};

NameChange.defaultProps = {
    uuid: null,
    surveyImport: null,
};

const mapStateToProps = (state, ownProps) => ({
    auth: state.auth,
    uuid: ownProps?.uuid || ownProps?.params?.uuid || null,
});

export default withNavigation(
    connect(
        mapStateToProps,
        {
            loading,
            getNameChange,
            saveNameChange,
        },
        null,
        { forwardRef: true }
    )(NameChange)
);
